/**
 * SharePoint Retrieval Module
 * Calls the Microsoft Graph Copilot Retrieval API and returns formatted extracts.
 * Used by the Client Tool event handler in chat-ui.ts — the Copilot Studio agent
 * decides when to call this and provides the cleaned query.
 */

import { acquireTokenForGraph } from "./auth";
import {
  trackRetrieval,
  trackRetrievalError,
  RetrievalResult,
} from "./telemetry";

// Site(s) to scope retrieval to — comma-separated SharePoint URLs.
// Set VITE_SHAREPOINT_SEARCH_SITES in .env (or Azure Static Web App settings).
export const RETRIEVAL_SITES: string[] = (
  import.meta.env.VITE_SHAREPOINT_SEARCH_SITES || ""
)
  .split(",")
  .map((s: string) => s.trim())
  .filter(Boolean);

export const RETRIEVAL_THRESHOLD = 0.45; // Minimum relevance score (0–1)
export const RETRIEVAL_TOP = 5; // Max results to request

/**
 * Call the Microsoft Graph Copilot Retrieval API and return formatted text
 * extracts prefixed with "Relevant SharePoint context:", or null if nothing
 * above the relevance threshold is found.
 */
export async function fetchRetrievalExtracts(
  query: string,
): Promise<string | null> {
  const token = await acquireTokenForGraph();

  const body: Record<string, unknown> = {
    queryString: query,
    dataSource: "sharePoint",
    top: RETRIEVAL_TOP,
  };

  // Scope to configured site(s)
  if (RETRIEVAL_SITES.length === 1) {
    body.filterExpression = `path:"${RETRIEVAL_SITES[0]}"`;
  } else if (RETRIEVAL_SITES.length > 1) {
    body.filterExpression = RETRIEVAL_SITES.map((s) => `path:"${s}"`).join(
      " OR ",
    );
  }

  console.group(`%c[Retrieval] API call`, "color:#0078d4;font-weight:bold");
  console.log("Query:", query);
  console.log("Request body:", JSON.stringify(body, null, 2));
  console.log("Threshold:", RETRIEVAL_THRESHOLD, "| Top:", RETRIEVAL_TOP);
  const t0 = performance.now();

  let durationMs = 0;
  let httpStatus = 0;

  try {
    const response = await fetch(
      "https://graph.microsoft.com/v1.0/copilot/retrieval",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
    );

    durationMs = Math.round(performance.now() - t0);
    httpStatus = response.status;
    console.log(
      `HTTP ${response.status} ${response.statusText} (${durationMs}ms)`,
    );

    if (!response.ok) {
      const errBody = await response.text().catch(() => "");
      console.error("[Retrieval] Error response body:", errBody);
      console.groupEnd();
      const err = new Error(
        `Retrieval API ${response.status}: ${response.statusText}`,
      );
      trackRetrievalError(query, err, durationMs);
      throw err;
    }

    const data = await response.json();
    console.log("Response body:", data);
    const items: any[] = data.value || [];
    console.log(`Total items returned: ${items.length}`);

    const tableRows: RetrievalResult[] = items.map((item) => ({
      name: item.resource?.name ?? "(no name)",
      url: item.resource?.webUrl ?? "",
      score: item.relevanceScore ?? 0,
      aboveThreshold: (item.relevanceScore ?? 0) >= RETRIEVAL_THRESHOLD,
      contentLength: (item.content ?? "").length,
    }));
    if (tableRows.length > 0) console.table(tableRows);

    const relevant = items
      .filter((item) => (item.relevanceScore ?? 0) >= RETRIEVAL_THRESHOLD)
      .sort((a, b) => (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0));

    trackRetrieval({
      query,
      requestBody: body,
      httpStatus,
      durationMs,
      totalItems: items.length,
      aboveThresholdItems: relevant.length,
      threshold: RETRIEVAL_THRESHOLD,
      top: RETRIEVAL_TOP,
      cacheHit: false, // cache removed — agent orchestrates calls
      results: tableRows,
    });

    if (relevant.length === 0) {
      console.log(
        `%cNo results above threshold (${RETRIEVAL_THRESHOLD}) — SP context skipped`,
        "color:orange",
      );
      console.groupEnd();
      return null;
    }

    const formatted = relevant
      .map((item) => {
        const source =
          item.resource?.name || item.resource?.webUrl || "SharePoint";
        const pct = ((item.relevanceScore ?? 0) * 100).toFixed(0);
        return `[Source: ${source} — ${pct}% match]\n${item.content}`;
      })
      .join("\n\n");

    console.log(
      `%c${relevant.length} extract(s) will be sent to agent`,
      "color:green;font-weight:bold",
    );
    relevant.forEach((item, i) => {
      console.group(
        `Extract ${i + 1}: ${item.resource?.name ?? "unknown"} (score: ${(item.relevanceScore ?? 0).toFixed(3)})`,
      );
      console.log("URL:", item.resource?.webUrl ?? "N/A");
      console.log(
        "Content preview:",
        (item.content ?? "").substring(0, 300) +
          ((item.content ?? "").length > 300 ? "…" : ""),
      );
      console.groupEnd();
    });
    console.groupEnd();

    return `📋 Relevant SharePoint context:\n\n${formatted}`;
  } catch (err) {
    durationMs = durationMs || Math.round(performance.now() - t0);
    if (httpStatus === 0) {
      console.error("[Retrieval] Network error:", err);
      console.groupEnd();
      trackRetrievalError(query, err, durationMs);
    }
    throw err;
  }
}
